import { useState, useRef, lazy, Suspense } from "react";
const ConsumerPrintSticker = lazy(() => import("@/components/inventory/ConsumerPrintSticker"));
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, DollarSign, Info, Share2, Printer, QrCode, ArrowLeftRight, Loader2, CalendarCheck, FileDown, ExternalLink, Facebook, Linkedin, Mail, MessageSquare, Link2, Twitter } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "@/lib/router-compat";
import type { RVUnit } from "@/hooks/useInventory";
import GetLowestPrice from "@/components/inventory/GetLowestPrice";
import { SaveToWishlistButton } from "@/components/inventory/WishlistButton";

export function ActionForm({
  title,
  displayTitle,
  icon: Icon,
  fields,
  rv,
  buttonClassName,
  buttonVariant = "outline",
  buttonSize = "default",
}: {
  title: string;
  displayTitle?: string;
  icon: React.ElementType;
  fields: string[];
  rv: RVUnit;
  buttonClassName?: string;
  buttonVariant?: "outline" | "default" | "secondary" | "ghost";
  buttonSize?: "default" | "sm" | "lg";
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const label = displayTitle ?? title;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    const fd = new FormData(formRef.current!);
    const firstName = (fd.get("first_name") as string) || "";
    const lastName = (fd.get("last_name") as string) || "";
    const email = (fd.get("email") as string) || "";
    const phone = (fd.get("phone") as string) || "";
    const message = (fd.get("message") as string) || "";
    const offer = (fd.get("offer") as string) || "";
    const friendEmail = (fd.get("friend_email") as string) || "";
    const tradeYear = (fd.get("trade_year") as string) || "";
    const tradeMake = (fd.get("trade_make") as string) || "";
    const tradeModel = (fd.get("trade_model") as string) || "";

    const sessionId = (() => { try { return sessionStorage.getItem("_pv_sid") || ""; } catch { return ""; } })();

    const formData: Record<string, unknown> = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      phone_mobile: phone,
      message,
      source_display: `${title} — ${rv.title}`,
      stock_number: rv.stock_number,
      vehicle_title: rv.title,
      vehicle_year: rv.year ?? "",
      vehicle_make: rv.make ?? "",
      vehicle_model: rv.model ?? "",
      vehicle_trim: rv.trim ?? "",
      vehicle_vin: rv.vin ?? "",
      vehicle_type: rv.rv_type ?? "",
      vehicle_condition: rv.condition ?? "",
      vehicle_price: rv.price ?? "",
      session_id: sessionId,
    };

    if (offer) formData['offer_amount'] = offer;
    if (friendEmail) formData['friend_email'] = friendEmail;
    if (tradeYear || tradeMake || tradeModel) {
      formData['trade_vehicle'] = { year: tradeYear, make: tradeMake, model: tradeModel };
    }

    try {
      const customerName = [firstName, lastName].filter(Boolean).join(" ") || null;
      const { error } = await supabase.from("form_submissions").insert({
        form_type: title,
        customer_name: customerName,
        customer_email: email || null,
        customer_phone: phone || null,
        form_data: formData as any,
      });

      if (error) throw error;

      toast({ title: "Submitted!", description: `Your ${label.toLowerCase()} request for ${rv.title} has been sent.` });
      setOpen(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message || "Failed to submit", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={buttonVariant}
          size={buttonSize}
          className={buttonClassName ?? "justify-start gap-2 text-sm"}
          aria-label={`${label} for ${rv.title}`}
        >
          <Icon className="h-4 w-4 text-primary" />
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[90dvh] overflow-y-auto flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase">{label}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mb-3">RE: {rv.title} (Stock #{rv.stock_number})</p>
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
          {fields.includes("name") && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label>First Name *</Label><Input name="first_name" required /></div>
              <div><Label>Last Name</Label><Input name="last_name" /></div>
            </div>
          )}
          {fields.includes("email") && <div><Label>Email *</Label><Input name="email" type="email" required /></div>}
          {fields.includes("phone") && <div><Label>Phone</Label><Input name="phone" type="tel" /></div>}
          {fields.includes("offer") && <div><Label>Your Offer *</Label><Input name="offer" type="number" required placeholder="$" /></div>}
          {fields.includes("friend_email") && <div><Label>Friend's Email *</Label><Input name="friend_email" type="email" required /></div>}
          {fields.includes("trade_year") && (
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Year</Label><Input name="trade_year" placeholder="2020" /></div>
              <div><Label>Make</Label><Input name="trade_make" placeholder="Keystone" /></div>
              <div><Label>Model</Label><Input name="trade_model" placeholder="Montana" /></div>
            </div>
          )}
          {fields.includes("message") && <div><Label>Message</Label><Textarea name="message" rows={3} /></div>}
          <Button type="submit" className="w-full font-heading uppercase" disabled={submitting}>
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function UnitActions({ rv }: { rv: RVUnit }) {
  const [shareFallbackOpen, setShareFallbackOpen] = useState(false);
  const [printRequest, setPrintRequest] = useState(0);
  const handlePrint = () => {
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 768px), (pointer: coarse)").matches;
    if (isMobile) {
      const slugOrId = rv.slug || rv.id;
      window.open(`/print/sticker/${slugOrId}`, "_blank", "noopener");
    } else {
      setPrintRequest((n) => n + 1);
    }
  };

  const handleShare = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const title = typeof document !== "undefined" ? document.title : rv.title;
    const text = `Check out this ${[rv.year, rv.make, rv.model].filter(Boolean).join(" ")} at Rex & Sons RVs`;
    const nav: any = typeof navigator !== "undefined" ? navigator : null;
    const payload = { title, text, url };
    const canUseNative =
      nav &&
      typeof nav.share === "function" &&
      (typeof nav.canShare !== "function" || nav.canShare(payload));

    if (canUseNative) {
      try {
        await nav.share(payload);
        return;
      } catch (err: any) {
        // User cancelled — do nothing, don't open fallback
        if (err?.name === "AbortError") return;
        // Any other error (iframe permissions, not allowed, etc.) → fallback modal
      }
    }
    setShareFallbackOpen(true);
  };

  const copyLink = async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied!", description: "Page URL copied to clipboard." });
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        toast({ title: "Link copied!", description: "Page URL copied to clipboard." });
      } catch {
        toast({ title: "Share", description: url });
      }
    }
  };

  const pageUrl = typeof window !== "undefined" ? window.location.href : "";
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(pageUrl)}`;
  const windowStickerUrl = rv.window_sticker_url;
  const shareTitle = typeof document !== "undefined" ? document.title : rv.title;
  const shareText = `Check out this ${[rv.year, rv.make, rv.model].filter(Boolean).join(" ")} at Rex & Sons RVs`;
  const primaryImage = (rv as any).primary_image_url || (rv as any).hero_image_url || "";
  const enc = encodeURIComponent;
  const shareTargets = [
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(pageUrl)}` },
    { name: "X / Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${enc(pageUrl)}&text=${enc(shareText)}` },
    { name: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(pageUrl)}` },
    { name: "Pinterest", icon: Share2, href: `https://pinterest.com/pin/create/button/?url=${enc(pageUrl)}&media=${enc(primaryImage)}&description=${enc(shareText)}` },
    { name: "Email", icon: Mail, href: `mailto:?subject=${enc(shareTitle)}&body=${enc(shareText + "\n\n" + pageUrl)}` },
    { name: "SMS", icon: MessageSquare, href: `sms:?&body=${enc(shareText + " " + pageUrl)}` },
  ];

  return (
    <div className="cta-block space-y-2 min-h-[340px]">
      {printRequest > 0 && (
        <Suspense fallback={null}>
          <ConsumerPrintSticker rv={rv} selectedImageIndex={0} printRequest={printRequest} />
        </Suspense>
      )}
      <GetLowestPrice
        title={rv.title}
        stockNumber={rv.stock_number}
        year={rv.year}
        make={rv.make}
        model={rv.model}
        variant="prominent"
      />
      {/* PRIMARY CTA — full width */}
      <ActionForm
        title="Contact Sales"
        displayTitle="Check Availability"
        icon={Phone}
        fields={["name", "email", "phone", "message"]}
        rv={rv}
        buttonVariant="default"
        buttonSize="lg"
        buttonClassName="w-full justify-center gap-2 font-heading uppercase tracking-wide"
      />
      {/* SECONDARY CTAs — 2-col grid */}
      <div className="grid grid-cols-2 gap-2">
        <ActionForm title="Request Info" displayTitle="Get More Details" icon={Info} fields={["name", "email", "message"]} rv={rv} />
        <ActionForm title="Value My Trade" displayTitle="Get Trade-In Value" icon={ArrowLeftRight} fields={["name", "email", "phone", "trade_year", "message"]} rv={rv} />
        <ActionForm title="Make an Offer" displayTitle="Make an Offer" icon={DollarSign} fields={["name", "email", "phone", "offer", "message"]} rv={rv} />
        <Button
          variant="outline"
          className="justify-start gap-2 text-sm"
          aria-label={`Share ${rv.title}`}
          onClick={handleShare}
        >
          <Share2 className="h-4 w-4 text-primary" />
          Share This RV
        </Button>
        <SaveToWishlistButton stockNumber={rv.stock_number} />
      <Dialog open={shareFallbackOpen} onOpenChange={setShareFallbackOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Share This RV</DialogTitle>
            <DialogDescription className="text-xs">
              {rv.title}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-2">
            {shareTargets.map((t) => (
              <a
                key={t.name}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center justify-center gap-1 rounded-md border border-input bg-background p-3 text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                <t.icon className="h-5 w-5 text-primary" />
                {t.name}
              </a>
            ))}
            <button
              type="button"
              onClick={copyLink}
              className="flex flex-col items-center justify-center gap-1 rounded-md border border-input bg-background p-3 text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Link2 className="h-5 w-5 text-primary" />
              Copy Link
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">
            Tip: on a phone, tap Share This RV to open your device's share sheet (Instagram, Messages, AirDrop, etc.).
          </p>
        </DialogContent>
      </Dialog>
        {windowStickerUrl && (
        <Dialog>
          <DialogTrigger asChild>
            <span className="relative inline-flex w-full">
              <span className="absolute inset-0 rounded-md bg-accent/50 animate-ping" aria-hidden="true" />
              <Button
                className="relative w-full justify-start gap-2 text-sm bg-accent text-accent-foreground hover:bg-accent/90 font-semibold shadow-md"
                aria-label={`View build sheet for ${rv.title}`}
              >
                <FileDown className="h-4 w-4" />
                View Build Sheet
              </Button>
            </span>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">Build Sheet</DialogTitle>
              <DialogDescription>
                <a href={windowStickerUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="h-3 w-3" /> Open full size
                </a>
                {" · "}
                <button onClick={() => {
                  const printWin = window.open(windowStickerUrl, "_blank");
                  if (printWin) { printWin.onload = () => printWin.print(); }
                }} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                  Print
                </button>
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-auto">
              <img src={windowStickerUrl} alt="Build Sheet" className="w-full h-auto rounded-sm" />
            </div>
          </DialogContent>
        </Dialog>
        )}
        <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline" className="justify-start gap-2 text-sm" aria-label={`Share or print ${rv.title}`}>
            <QrCode className="h-4 w-4 text-primary" />
            QR / Print
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase">Share This Unit</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-3">{rv.title}</p>
          <img src={qrUrl} alt="QR Code" className="mx-auto h-48 w-48 rounded-sm border" />
          <p className="text-xs text-muted-foreground mt-2">Scan to view on your phone</p>
          <Button onClick={handlePrint} className="mt-3 w-full font-heading uppercase gap-2">
            <Printer className="h-4 w-4" /> Print This Page
          </Button>
        </DialogContent>
        </Dialog>
        <Button
          variant="outline"
          className="justify-start gap-2 text-sm"
          aria-label={`Print ${rv.title}`}
          onClick={handlePrint}
        >
          <Printer className="h-4 w-4 text-primary" />
          Print
        </Button>
      </div>
      {/* Walk-Through CTA — full width */}
      <Button asChild variant="default" className="w-full justify-center gap-2 font-heading uppercase tracking-wide" aria-label={`Schedule a walk-through for ${rv.title}`}>
        <Link to="/schedule-appointment">
          <CalendarCheck className="h-4 w-4" />
          Schedule a Walk-Through
        </Link>
      </Button>
      <GetLowestPrice
        title={rv.title}
        stockNumber={rv.stock_number}
        year={rv.year}
        make={rv.make}
        model={rv.model}
        variant="prominent"
      />
    </div>
  );
}

/** Compact CTA row for repeated mid-page / bottom-of-page placements. */
export function UnitCtaRow({
  rv,
  variant = "availability-price",
}: {
  rv: RVUnit;
  variant?: "availability-price" | "availability-walkthrough";
}) {
  if (rv.status === "sold") return null;
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 min-h-[72px]">
      <ActionForm
        title="Contact Sales"
        displayTitle="Check Availability"
        icon={Phone}
        fields={["name", "email", "phone", "message"]}
        rv={rv}
        buttonVariant="default"
        buttonSize="lg"
        buttonClassName="w-full justify-center gap-2 font-heading uppercase tracking-wide"
      />
      {variant === "availability-price" ? (
        <GetLowestPrice
          title={rv.title}
          stockNumber={rv.stock_number}
          year={rv.year}
          make={rv.make}
          model={rv.model}
          variant="prominent"
        />
      ) : (
        <Button asChild variant="default" size="lg" className="w-full justify-center gap-2 font-heading uppercase tracking-wide" aria-label={`Schedule a walk-through for ${rv.title}`}>
          <Link to="/schedule-appointment">
            <CalendarCheck className="h-4 w-4" />
            Schedule a Walk-Through
          </Link>
        </Button>
      )}
    </div>
  );
}
